/**
 * G7 — shared guard SSOT for OIDC land pending + aggregate budget timeout.
 *
 * Subscribes to window.search (strip events) and schedules give-up when the
 * aggregate land budget elapses so auth routes cannot spin 60s+ on sign-in.
 */

import { useEffect, useMemo, useState, useSyncExternalStore } from 'react';
import {
  giveUpOidcLandAggregateBudget,
  hasOidcLandingPending,
  msUntilOidcLandAggregateBudgetExhausted,
  noteOidcLandAggregateStart,
  OIDC_LAND_QUERY_CHANGED_EVENT,
  readOidcLandGuardPending,
  type OidcLandGuardPendingState,
} from './oidcCookieLand.js';

function subscribeOidcLandWindowSearch(onStoreChange: () => void): () => void {
  window.addEventListener(OIDC_LAND_QUERY_CHANGED_EVENT, onStoreChange);
  return () => {
    window.removeEventListener(OIDC_LAND_QUERY_CHANGED_EVENT, onStoreChange);
  };
}

function getOidcLandWindowSearchSnapshot(): string {
  return window.location.search;
}

function getOidcLandWindowSearchServerSnapshot(): string {
  return '';
}

export function useOidcLandGuardPending(): OidcLandGuardPendingState {
  const windowOidcSearch = useSyncExternalStore(
    subscribeOidcLandWindowSearch,
    getOidcLandWindowSearchSnapshot,
    getOidcLandWindowSearchServerSnapshot,
  );
  const [budgetTick, setBudgetTick] = useState(0);

  useEffect(() => {
    if (!hasOidcLandingPending(windowOidcSearch)) {
      return;
    }
    noteOidcLandAggregateStart();
    const remainingMs = msUntilOidcLandAggregateBudgetExhausted();
    if (remainingMs === null) {
      return;
    }
    if (remainingMs <= 0) {
      const immediateTimer = window.setTimeout(() => {
        giveUpOidcLandAggregateBudget();
        setBudgetTick((n) => n + 1);
      }, 0);
      return () => {
        window.clearTimeout(immediateTimer);
      };
    }
    const timer = window.setTimeout(() => {
      giveUpOidcLandAggregateBudget();
      setBudgetTick((n) => n + 1);
    }, remainingMs);
    return () => {
      window.clearTimeout(timer);
    };
  }, [windowOidcSearch]);

  return useMemo(() => {
    void budgetTick;
    return readOidcLandGuardPending(windowOidcSearch);
  }, [windowOidcSearch, budgetTick]);
}
